import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Collapse,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

const Row = ({ row, level = 0, expandedRows, onToggleExpand }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = row.children && row.children.length > 0;
  const isExpanded = expandedRows.includes(row.node_id);
  
  const handleToggle = () => {
    setOpen(!open);
    onToggleExpand(row.node_id);
  };
  
  const getNodeIcon = () => {
    if (!hasChildren) return null;
    return isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />;
  };
  
  return (
    <>
      <TableRow 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          backgroundColor: level === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
        }}
      >
        <TableCell component="th" scope="row" sx={{ pl: level * 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasChildren && (
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{ mr: 1 }}
              >
                {getNodeIcon()}
              </IconButton>
            )}
            {!hasChildren && <Box sx={{ width: 40 }} />}
            <Typography variant="body2">{row.name}</Typography>
          </Box>
        </TableCell>
        <TableCell>{row.sku || '-'}</TableCell>
        <TableCell>{row.level === 'department' ? row.name : ''}</TableCell>
        <TableCell>{row.level === 'category' ? row.name : ''}</TableCell>
        <TableCell>{row.level === 'subcategory' ? row.name : ''}</TableCell>
        <TableCell align="right">{row.sku_count || 0}</TableCell>
        <TableCell align="right">{row.total_quantity?.toLocaleString() || 0}</TableCell>
        <TableCell align="right">${row.total_value?.toLocaleString() || '0'}</TableCell>
      </TableRow>
      {hasChildren && isExpanded && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Table size="small" aria-label="children">
                  <TableBody>
                    {row.children.map((child) => (
                      <Row 
                        key={child.node_id} 
                        row={child} 
                        level={level + 1} 
                        expandedRows={expandedRows} 
                        onToggleExpand={onToggleExpand} 
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const InventoryHierarchyGrid = ({ hierarchy }) => {
  const [expandedRows, setExpandedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const handleToggleExpand = (nodeId) => {
    setExpandedRows((prev) =>
      prev.includes(nodeId)
        ? prev.filter((id) => id !== nodeId)
        : [...prev, nodeId]
    );
  };
  
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };
  
  // Ensure hierarchy is an array
  const hierarchyArray = Array.isArray(hierarchy) ? hierarchy : [hierarchy];
  
  return (
    <Box>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search inventory hierarchy..."
        value={searchTerm}
        onChange={handleSearch}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <TableContainer component={Paper}>
        <Table aria-label="inventory hierarchy">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Subcategory</TableCell>
              <TableCell align="right">SKU Count</TableCell>
              <TableCell align="right">Total Units</TableCell>
              <TableCell align="right">Total Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hierarchyArray.map((row) => (
              <Row 
                key={row.node_id} 
                row={row} 
                expandedRows={expandedRows} 
                onToggleExpand={handleToggleExpand} 
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InventoryHierarchyGrid; 